import {
	AbstractControl,
	ValidationErrors,
	ValidatorFn
}                                       from '@angular/forms';
import { IDateRelationValidatorConfig } from '@shared/forms/validators/models/date-relation-validator.model';
import {
	DateUtils,
	ObjectUtils
}                                       from '@utils/helpers';
import { DateRelationEnum }             from '@utils/helpers/date-utils';

/**
 * Returns a validator function for date relations. The validator function will
 * evaluate the relation between the two dates and will check if this date
 * relation is among the `validDateRelations`. If there is no form control value,
 * the validation will fail anyway.
 *
 * @param config.errorKey The key that holds the error message in the i18n maps
 * @param config.ignoreEmpty If true, the validation will pass over null or undefined control date value without errors, default is false
 * @param config.negateValidationResult Negates (inverses) the validation result (i.e. if the result is "valid", then it is turned to "invalid", and vice versa)
 * @param config.precision The time unit-level by which the comparison is done. Default is `MILLISECONDS`.
 * @param config.referenceDate The date object used for comparison with the date object hold by the form control value
 * @param config.validDateRelations An array of DateRelationEnum constants defining the valid date relations (handled as OR operator)
 */
export function dateRelationValidator(
	config: IDateRelationValidatorConfig
): ValidatorFn {
	return ( control: AbstractControl ): ValidationErrors | null => {
		const {
			errorKey,
			ignoreEmpty,
			negateValidationResult,
			precision,
			referenceDate,
			validDateRelations
		} = config ?? {};

		/*
		 * TODO -- check when unit tests for this utils are implemented --
		 *  It seems the validation will always fail if there actually is an empty
		 *  date field value, because then `dateRelation` will be `undefined`.
		 *  Currently it probably only works because the date form fields are only
		 *  validated if there is a value filled (i.e. date validation is
		 *  CONDITIONAL, and the condition includes checking whether the form
		 *  control to be validated actually has/holds a value).
		 */
		const dateRelation: DateRelationEnum = DateUtils.getDateRelation( control?.value, referenceDate, precision );
		const dateRelationIsValid: boolean = validDateRelations?.includes( dateRelation );
		const emptyIsValid: boolean = ignoreEmpty ? true : !ObjectUtils.isNullish( control?.value );
		let isValid: boolean = dateRelationIsValid;

		if ( negateValidationResult ) {
			isValid = !isValid;
		}

		isValid = isValid && emptyIsValid;

		return isValid
					 ? null
					 : { [ errorKey ]: true };
	};
}
